"use server";

import { randomBytes } from "crypto";
import bcrypt from "bcryptjs";
import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";
import { z } from "zod";
import { pool } from "./db";
import { createSession, deleteSession, requireAdmin } from "./session";
import { ESTADOS_VENTA, METODOS_PAGO, type EstadoVenta } from "./format";

export type FormState =
  | { error?: string; fieldErrors?: Record<string, string[] | undefined>; values?: Record<string, string> }
  | undefined;

// Devuelve lo enviado para rellenar el formulario de nuevo tras un error (React lo resetea al terminar la acción).
function valuesOf(formData: FormData) {
  const values: Record<string, string> = {};
  formData.forEach((v, k) => {
    if (typeof v === "string" && !k.startsWith("$")) values[k] = v;
  });
  return values;
}

/* ---------- Autenticación ---------- */

export async function login(_prev: FormState, formData: FormData): Promise<FormState> {
  const username = String(formData.get("username") ?? "").trim();
  const password = String(formData.get("password") ?? "");

  const { rows } = await pool.query<{ id: number; username: string; password_hash: string }>(
    "SELECT id, username, password_hash FROM admins WHERE username = $1",
    [username],
  );
  const admin = rows[0];
  if (!admin || !(await bcrypt.compare(password, admin.password_hash))) {
    return { error: "Usuario o contraseña incorrectos", values: { username } };
  }

  await createSession({ adminId: admin.id, username: admin.username });
  redirect("/admin");
}

export async function logout() {
  await deleteSession();
  redirect("/login");
}

/* ---------- Profesores ---------- */

const profesorSchema = z.object({
  nombre: z.string().trim().min(2, "Indica el nombre completo"),
  email: z.email("Email no válido"),
  telefono: z.string().trim().max(30).optional().transform((v) => v || null),
  materia: z.string().trim().min(2, "Indica la materia"),
  nivel: z.string().trim().min(1, "Indica el nivel"),
  descripcion: z.string().trim().max(2000).default(""),
  precio_hora: z.coerce.number({ error: "Precio no válido" }).min(0, "El precio no puede ser negativo"),
  foto_url: z
    .union([z.url("URL no válida"), z.literal("")])
    .optional()
    .transform((v) => v || null),
  activo: z.boolean(),
});

function parseProfesor(formData: FormData) {
  return profesorSchema.safeParse({
    ...Object.fromEntries(formData),
    activo: formData.get("activo") === "on",
  });
}

export async function crearProfesor(_prev: FormState, formData: FormData): Promise<FormState> {
  await requireAdmin();
  const parsed = parseProfesor(formData);
  if (!parsed.success) return { fieldErrors: z.flattenError(parsed.error).fieldErrors, values: valuesOf(formData) };

  const p = parsed.data;
  await pool.query(
    `INSERT INTO profesores (nombre, email, telefono, materia, nivel, descripcion, precio_hora, foto_url, activo)
     VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9)`,
    [p.nombre, p.email, p.telefono, p.materia, p.nivel, p.descripcion, p.precio_hora, p.foto_url, p.activo],
  );
  revalidatePath("/", "layout");
  redirect("/admin/profesores");
}

export async function editarProfesor(id: number, _prev: FormState, formData: FormData): Promise<FormState> {
  await requireAdmin();
  const parsed = parseProfesor(formData);
  if (!parsed.success) return { fieldErrors: z.flattenError(parsed.error).fieldErrors, values: valuesOf(formData) };

  const p = parsed.data;
  await pool.query(
    `UPDATE profesores SET nombre=$1, email=$2, telefono=$3, materia=$4, nivel=$5, descripcion=$6,
       precio_hora=$7, foto_url=$8, activo=$9 WHERE id=$10`,
    [p.nombre, p.email, p.telefono, p.materia, p.nivel, p.descripcion, p.precio_hora, p.foto_url, p.activo, id],
  );
  revalidatePath("/", "layout");
  redirect("/admin/profesores");
}

export async function toggleProfesor(id: number) {
  await requireAdmin();
  await pool.query("UPDATE profesores SET activo = NOT activo WHERE id = $1", [id]);
  revalidatePath("/", "layout");
}

export async function eliminarProfesor(id: number) {
  await requireAdmin();
  const { rows } = await pool.query("SELECT 1 FROM ventas WHERE profesor_id = $1 LIMIT 1", [id]);
  if (rows.length) {
    // Con ventas asociadas no se borra: se desactiva para conservar el histórico.
    await pool.query("UPDATE profesores SET activo = false WHERE id = $1", [id]);
  } else {
    await pool.query("DELETE FROM profesores WHERE id = $1", [id]);
  }
  revalidatePath("/", "layout");
}

/* ---------- Ventas ---------- */

const ventaSchema = z.object({
  alumno_nombre: z.string().trim().min(2, "Indica tu nombre"),
  alumno_email: z.email("Email no válido"),
  alumno_telefono: z.string().trim().max(30).optional().transform((v) => v || null),
  horas: z.coerce.number().int("Número de horas no válido").min(1, "Mínimo 1 hora").max(100, "Máximo 100 horas"),
  metodo_pago: z.enum(Object.keys(METODOS_PAGO) as [keyof typeof METODOS_PAGO], { error: "Elige un método de pago" }),
  notas: z.string().trim().max(1000).optional().transform((v) => v || null),
  acepta: z.literal("on", { error: "Debes aceptar las condiciones" }),
});

export async function crearVenta(profesorId: number, _prev: FormState, formData: FormData): Promise<FormState> {
  const parsed = ventaSchema.safeParse(Object.fromEntries(formData));
  if (!parsed.success) return { fieldErrors: z.flattenError(parsed.error).fieldErrors, values: valuesOf(formData) };

  const { rows } = await pool.query<{ precio_hora: string }>(
    "SELECT precio_hora FROM profesores WHERE id = $1 AND activo = true",
    [profesorId],
  );
  if (!rows[0]) return { error: "Este profesor ya no está disponible", values: valuesOf(formData) };

  const v = parsed.data;
  const precio = Number(rows[0].precio_hora);
  const total = Math.round(precio * v.horas * 100) / 100;
  const codigo = `SN-${randomBytes(4).toString("hex").toUpperCase()}`;

  await pool.query(
    `INSERT INTO ventas (codigo, profesor_id, alumno_nombre, alumno_email, alumno_telefono, horas,
       precio_hora, total, metodo_pago, notas)
     VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)`,
    [codigo, profesorId, v.alumno_nombre, v.alumno_email, v.alumno_telefono, v.horas, precio, total, v.metodo_pago, v.notas],
  );
  revalidatePath("/admin", "layout");
  redirect(`/venta/${codigo}`);
}

export async function cambiarEstadoVenta(id: number, estado: EstadoVenta) {
  await requireAdmin();
  if (!(estado in ESTADOS_VENTA)) throw new Error("Estado no válido");
  await pool.query(
    `UPDATE ventas SET estado = $1,
       concretada_at = CASE WHEN $1 = 'concretada' THEN now() ELSE NULL END
     WHERE id = $2`,
    [estado, id],
  );
  revalidatePath("/", "layout");
}
